import useMeicWebsocketDataStore from '../../Stores/MEIC_Data_Store';

export function useMeicSummary() {
  const tradesById      = useMeicWebsocketDataStore((s) => s.tradesById);
  const dailySummary    = useMeicWebsocketDataStore((s) => s.dailySummary);
  const marketState     = useMeicWebsocketDataStore((s) => s.marketState);
  const dailyTrades     = useMeicWebsocketDataStore((s) => s.dailyTrades);
  const dailyTradesDate = useMeicWebsocketDataStore((s) => s.dailyTradesDate);

  // ── Regime / market state ─────────────────────
  const regime = marketState?.regime ?? null;
  const regimeLabel =
    typeof regime === 'number' ? `Regime ${regime}` : 'Regime —';

  const regimeTextTemplate = '';

  // ── DB snapshot trades (today, base layer) ────
  // NOTE: dailyTrades are now already normalized in the store
  // (entryTime, optionType, shortStrike, etc.), but we support
  // both camelCase and old snake_case just in case.
  const historicalTradeRows = (dailyTrades || []).map((row, idx) => {
    const id =
      row.id != null
        ? String(row.id)
        : `${row.entryTime || row.entry_timestamp_et || 'unknown'}_${row.shortStrike ?? row.short_strike}_${row.longStrike ?? row.long_strike}_${idx}`;

    const entryTime =
      row.entryTime ??
      row.entry_timestamp_et ??
      null;

    const optionType =
      row.optionType ??
      row.option_type ??
      null;

    const shortStrike =
      row.shortStrike ??
      row.short_strike ??
      null;

    const longStrike =
      row.longStrike ??
      row.long_strike ??
      null;

    const premium =
      row.premium != null ? row.premium : null;

    const pnlDollars =
      row.pnlDollars ??
      row.net_profit_loss ??
      row.profit_loss ??
      null;

    const status =
      row.status ??
      (row.outcome ? 'CLOSED' : null);

    return {
      id,
      entryTime,
      optionType,
      shortStrike,
      longStrike,
      premium,
      outcome: row.outcome || null,
      pnlDollars,
      status,
      spreadMark: row.spreadMark ?? null,
      spxStopEstimateLinear: row.spxStopEstimateLinear ?? null,
      reason: row.reason || null,
    };
  });

  // ── Live trades from WS (overlays / updates) ──
  const liveTradesFromWs = Object.values(tradesById || {})
    .map((t) => {
      const id = t.id != null ? String(t.id) : null;

      const pnlDollars =
        typeof t.realizedPnlDollars === 'number'
          ? t.realizedPnlDollars
          : typeof t.currentPnlDollars === 'number'
          ? t.currentPnlDollars
          : t.pnlDollars ?? null;

      const entryTime =
        t.entryTime ??
        t.entryTimestampEt ??
        null;

      return {
        id,
        entryTime,
        optionType: t.optionType || null,
        shortStrike: t.shortStrike ?? null,
        longStrike: t.longStrike ?? null,
        premium: t.premium ?? null,
        spreadMark: t.spreadMark ?? null,
        pnlDollars,
        spxStopEstimateLinear: t.spxStopEstimateLinear ?? null,
        status: t.status || null,
        reason: t.reason || null,
        outcome: t.outcome || null,
      };
    })
    .filter((row) => row.id !== null); // must have id

  // ── Merge DB snapshot + WS by id ──────────────
  const combinedById = new Map();

  // 1) Seed with DB rows
  for (const row of historicalTradeRows) {
    combinedById.set(row.id, { ...row });
  }

  // 2) Overlay WS rows (same id => update, new id => append)
  for (const live of liveTradesFromWs) {
    const existing = combinedById.get(live.id) || {};

    const merged = {
      ...existing,
      ...live,
      // prefer live values when present
      pnlDollars: live.pnlDollars ?? existing.pnlDollars ?? null,
      entryTime:  live.entryTime  || existing.entryTime  || null,
      status:     live.status     || existing.status     || null,
      outcome:    live.outcome    || existing.outcome    || null,
    };

    combinedById.set(live.id, merged);
  }

  const liveTradeRows = Array.from(combinedById.values());

  // ── Daily summary stats ───────────────────────
  const summaryStats = dailySummary
    ? {
        date: dailySummary.date || null,
        totalRealizedPnlDollars: dailySummary.totalRealizedPnlDollars ?? 0,
        totalTradesClosed: dailySummary.totalTradesClosed ?? 0,
        numWins:   dailySummary.numWins ?? 0,
        numLosses: dailySummary.numLosses ?? 0,
        numStops:  dailySummary.numStops ?? 0,
        numExpires: dailySummary.numExpires ?? 0,
        avgWinPnlDollars:  dailySummary.avgWinPnlDollars ?? 0,
        avgLossPnlDollars: dailySummary.avgLossPnlDollars ?? 0,
      }
    : null;

  return {
    regime,
    regimeLabel,
    regimeTextTemplate,
    summaryStats,
    liveTradeRows,        // ✅ merged DB + WS
    historicalTradeRows,  // DB-only, if you ever need it
    dailyTradesDate,
  };
}